import React, { useState, useRef, useEffect, useMemo } from 'react';
import {
    Search,
    Plus,
    MoreHorizontal,
    ChevronLeft,
    ChevronRight,
    X,
    Copy,
    SquareArrowOutUpRight,
    Funnel,
    CircleCheck, Info
} from 'lucide-react';
import { useHistory, useLocation } from "react-router-dom";
import apiHelper from '../../apiHelper';
import { useLoader } from '../../components/GlobalLoader';
import noBrandImage from '../../assets/img/brand/no-brand-img.png';
import emptySearchImg from '../../assets/img/brand/empty-search.png';
import AlertNotification from '../../components/AlertNotification';
import ConfirmDeactivateModal from '../../components/ConfirmDeactivateModal';
import DynamicTable from '../../components/DynamicTable';
import SearchBox from '../../components/SearchBox';
import Pagination from '../../components/Pagination';

const PAGE_SIZE = 10; // Display all 10 rows on one page

export default function Branch() {
    const [activeTab, setActiveTab] = useState('branches');
    const history = useHistory();
    const location = useLocation();
    const filterButtonRef = useRef(null);
    const filterPopupRef = useRef(null);
    const [filterPopupOpen, setFilterPopupOpen] = useState(false);
    const [searchStatus, setSearchStatus] = useState([]);
    const [ParentBrand, setParentBrand] = useState([]);
    const filterCount = [...searchStatus, ...ParentBrand].filter(Boolean).length;
    const [selectedStatuses, setSelectedStatuses] = useState([]);
    const [selectedParentBrands, setSelectedParentBrands] = useState([]);
    const [filterPosition, setFilterPosition] = useState({ top: 0, left: 0 });

    // Commented out archived filter related code
    /*
    const archivedFilterButtonRef = useRef(null);
    const archivedFilterPopupRef = useRef(null);
    const [archivedFilterPopupOpen, setArchivedFilterPopupOpen] = useState(false);
    const [archivedFilterPosition, setArchivedFilterPosition] = useState({ top: 0, left: 0 });
    const [archivedSearchStatus, setArchivedSearchStatus] = useState([]);
    const [archivedSearchParentCategory, setArchivedSearchParentCategory] = useState([]);
    const [archivedParentCategorySearchTerm, setArchivedParentCategorySearchTerm] = useState('');
    const archivedFilterCount = [...archivedSearchStatus, ...archivedSearchParentCategory].filter(Boolean).length;
    */

    // Breadcrumbs (you can adjust these if needed)
    const breadcrumbs = ["Settings", "Branches"];

    // Search, pagination, and multi‐selection state
    const [searchTerm, setSearchTerm] = useState('');
    const [activeSearchTerm, setActiveSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedIds, setSelectedIds] = useState([]);
    const [statusFilter, setStatusFilter] = useState('');
    const { showLoader, hideLoader } = useLoader();
    const [searchInput, setSearchInput] = useState('');


    // Commented out archived branch tab state
    /*
    // Archived Branch Tab
    const [archivedSearchTerm, setArchivedSearchTerm] = useState('');
    const [archivedCurrentPage, setArchivedCurrentPage] = useState(1);
    const [archivedSelectedIds, setarchivedSelectedIds] = useState([]);
    const [archivedStatusFilter, setArchivedStatusFilter] = useState('');
    */

    // "Suppress highlight" flag: if true, even checked rows will not be highlighted
    const [suppressHighlight, setSuppressHighlight] = useState(false);

    // Single‐row Drawer state (unchanged)
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [drawerTab, setDrawerTab] = useState('general');
    const [selectedRow, setSelectedRow] = useState(null);

    const [branchData, setBranchData] = useState([]);
    const [loading, setLoading] = useState(true);

    // Commented out archived data state
    /*
    const [archivedData, setArchivedData] = useState([]);
    */

    const [successMessage, setSuccessMessage] = useState('');
    const [validationError, setValidationError] = useState([]);

    // Commented out archived branches state
    /*
    const [archivedBranches, setArchivedBranches] = useState([]);
    */

    const [selectedBranchId, setSelectedBranchId] = useState(null);

    const openIdRef = useRef(null);
    const [copied, setCopied] = useState(false);

    const [sortField, setSortField] = useState('branch_name');
    const [sortOrder, setSortOrder] = useState('asc'); // 'asc' or 'desc'

    // New state for toast notification and confirmation modal
    const [showToast, setShowToast] = useState(false);
    const [toastAction, setToastAction] = useState('');
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [branchToDeactivate, setBranchToDeactivate] = useState(null);

  useEffect(() => {
          console.log('location.state on mount:', location.state);
  
          if (location && location.state && location.state.success) {
              setSuccessMessage(location.state.success);
              history.replace(location.pathname, {});
  
              const t = setTimeout(() => setSuccessMessage(''), 5000);
              return () => clearTimeout(t);
          }
      }, [location, history]);
  
  
      useEffect(() => {
          if (successMessage) {
              const t = setTimeout(() => setSuccessMessage(""), 5000);
              return () => clearTimeout(t);
          }
      }, [successMessage]);


    const handleCopy = async (textToCopy) => {
        if (!textToCopy) {
            console.error('No text to copy');
            alert('Failed to copy: No text available.');
            return;
        }

        // Use Clipboard API if available and secure
        if (window.isSecureContext && navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
            try {
                await navigator.clipboard.writeText(textToCopy);
                setCopied(true);
                setTimeout(() => setCopied(false), 2000); // Reset after 2 seconds
            } catch (err) {
                console.error('Clipboard API failed:', err);
                fallbackCopyTextToClipboard(textToCopy);
            }
        } else {
            console.warn('Clipboard API not available or not in secure context, using fallback');
            fallbackCopyTextToClipboard(textToCopy);
        }
    };

    const fallbackCopyTextToClipboard = (text) => {
        try {
            const textArea = document.createElement('textarea');
            textArea.value = text;
            textArea.style.position = 'fixed';
            textArea.style.top = '0';
            textArea.style.left = '0';
            textArea.style.opacity = '0';
            document.body.appendChild(textArea);
            textArea.focus();
            textArea.select();

            const successful = document.execCommand('copy');
            document.body.removeChild(textArea);

            if (successful) {
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
            } else {
                console.error('Fallback copy failed');
                alert('Failed to copy text. Please copy manually.');
            }
        } catch (err) {
            console.error('Fallback copy error:', err);
            alert('Failed to copy text. Please copy manually.');
        }
    };

    useEffect(() => {
        const fetchBranches = async () => {
            try {
                showLoader(); // Show loader when API call starts
                const response = await apiHelper('entities/branches/', 'GET');
                console.log('hhhh', response);
                if (response.ok) {
                    const data = await response.json();
                    console.log('hhhh', data);
                    setBranchData(data);
                }
            } catch (error) {
                console.error("Failed to fetch branches:", error);
            } finally {
                setLoading(false);
                hideLoader(); // Hide loader when API call completes (success or error)
            }
        };

        fetchBranches();
    }, []);

    // Handle Enter key for search
    const handleSearch = (e) => {
        if (e.key === 'Enter') {
            setActiveSearchTerm(searchTerm);
            setCurrentPage(1);
        }
    };

    // Handle search input change
    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
        // Clear active search term when user starts typing new search
        if (activeSearchTerm && e.target.value !== activeSearchTerm) {
            setActiveSearchTerm('');
        }
    };

    // Clear search term
    const handleClearSearch = () => {
        setSearchTerm('');
        setActiveSearchTerm('');
    };

    const filtered = useMemo(() => {
        const term = activeSearchTerm.toLowerCase();

        let result = branchData
            .filter(d => !d.is_archived) // ✅ Exclude archived branches here
            .filter(d => {
                const name = d.branch_name || '';
                const status = d.status ? 'Active' : 'Inactive';
                const workplace = d.workplace_name || '';
                const id = d.id?.toString() || '';

                const searchMatch =
                    name.toLowerCase().includes(term) ||
                    status.toLowerCase().includes(term) ||
                    workplace.toLowerCase().includes(term) ||
                    id.includes(term);
                const statusMatch =
                    selectedStatuses.length === 0 || selectedStatuses.includes(status);

                return searchMatch && statusMatch;
            });

        // ✅ Apply sorting like brandsData
        if (sortField) {
            result = [...result].sort((a, b) => {
                let aVal = a[sortField];
                let bVal = b[sortField];

                if (aVal == null) aVal = '';
                if (bVal == null) bVal = '';

                if (typeof aVal === 'number' && typeof bVal === 'number') {
                    return sortOrder === 'asc' ? aVal - bVal : bVal - aVal;
                }

                aVal = aVal.toString().toLowerCase();
                bVal = bVal.toString().toLowerCase();

                return sortOrder === 'asc'
                    ? aVal.localeCompare(bVal)
                    : bVal.localeCompare(aVal);
            });
        }

        return result;
    }, [branchData, activeSearchTerm, selectedStatuses, sortField, sortOrder]);

    // Commented out archived filtered data
    /*
    // Filtered + sorted data for archived
    const archivedFiltered = useMemo(() => {
        const term = archivedSearchTerm.toLowerCase();

        let result = archivedData.filter(branch => {
            const name = branch.branch_name || '';
            const id = branch.id?.toString() || '';
            const workplace = branch.workplace_name || '';
            const status = branch.status ? 'Active' : 'Inactive';

            const matchesSearch =
                name.toLowerCase().includes(term) ||
                id.toLowerCase().includes(term) ||
                workplace.toLowerCase().includes(term) ||
                status.toLowerCase().includes(term);

            const matchesStatus =
                archivedSearchStatus.length === 0 || archivedSearchStatus.includes(status);

            return matchesSearch && matchesStatus;
        });

        // ✅ Apply sorting
        if (sortField) {
            result = [...result].sort((a, b) => {
                let aVal = a[sortField];
                let bVal = b[sortField];

                if (aVal == null) aVal = '';
                if (bVal == null) bVal = '';

                if (typeof aVal === 'number' && typeof bVal === 'number') {
                    return sortOrder === 'asc' ? aVal - bVal : bVal - aVal;
                }

                aVal = aVal.toString().toLowerCase();
                bVal = bVal.toString().toLowerCase();

                return sortOrder === 'asc'
                    ? aVal.localeCompare(bVal)
                    : bVal.localeCompare(aVal);
            });
        }

        return result;
    }, [archivedData, archivedSearchTerm, archivedSearchStatus, sortField, sortOrder]);
    */


    const handleCheckboxChange = (setter, value) => (e) => {
        const isChecked = e.target.checked;
        setter(prev =>
            isChecked
                ? [...prev, value]
                : prev.filter(v => v !== value)
        );
    };

    useEffect(() => {
        if (filterPopupOpen && filterButtonRef.current) {
            const rect = filterButtonRef.current.getBoundingClientRect();
            setFilterPosition({
                top: rect.bottom + window.scrollY,
                left: rect.left + window.scrollX
            });
        }
    }, [filterPopupOpen]);

    // Commented out archived filter position effect
    /*
    useEffect(() => {
        if (archivedFilterPopupOpen && archivedFilterButtonRef.current) {
            const rect = archivedFilterButtonRef.current.getBoundingClientRect();
            setArchivedFilterPosition({
                top: rect.bottom + window.scrollY,
                left: rect.left + window.scrollX
            });
        }
    }, [archivedFilterPopupOpen]);
    */

    // Commented out archived pagination calculations
    /*
    console.log('📄 Total archived filtered:', archivedFiltered.length);
    const archivedTotalPages = Math.ceil(archivedFiltered.length / PAGE_SIZE);
    console.log('📑 Archived total pages:', archivedTotalPages);

    const archivedPageData = archivedFiltered.slice(
        (archivedCurrentPage - 1) * PAGE_SIZE,
        archivedCurrentPage * PAGE_SIZE
    );
    console.log('📃 Archived page data:', archivedPageData);
    */

    // Pagination calculations
    const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
    const pageData = filtered.slice(
        (currentPage - 1) * PAGE_SIZE,
        currentPage * PAGE_SIZE
    );

    // Toggle selecting a single row (checkbox)
    const toggleSelect = id => {
        setSelectedIds(ids => {
            const newIds = ids.includes(id)
                ? ids.filter(x => x !== id)
                : [...ids, id];

            // As soon as user toggles a checkbox, we should no longer suppress highlight
            setSuppressHighlight(false);
            return newIds;
        });
    };

    // Toggle "select all" on the current page
    const toggleAll = () => {
        setSelectedIds(prev => {
            const next =
                prev.length === pageData.length
                    ? []
                    : pageData.map(d => d.branch_id);
            setSuppressHighlight(false);
            return next;
        });
    };

    // When a row (except its checkbox) is clicked, open the single‐row drawer
    const onRowClick = (row) => {
        setSelectedRow(row);
        // setSelectedIds([row.branch_id]);
        setDrawerTab('general');
        setDrawerOpen(true);
    };


    // Dropdown (ellipsis) state & refs (unchanged)
    const [dropdownOpenId, setDropdownOpenId] = useState(null);
    const dropdownRef = useRef(null);
    const buttonRefs = useRef({});
    const drawerRef = useRef(null);
    const [branchesData, setBranchesData] = useState([]);

    const currentRow = branchesData.find(e => e.branch_id === dropdownOpenId);

    useEffect(() => {
        function handleClickOutsideDrawer(event) {
            if (
                drawerOpen &&
                drawerRef.current &&
                !drawerRef.current.contains(event.target)
            ) {
                setDrawerOpen(false);
                setSelectedRow(null);
                setSelectedIds(ids =>
                    selectedRow ? ids.filter(id => id !== selectedRow.branch_id) : ids
                );
            }
        }
        document.addEventListener('mousedown', handleClickOutsideDrawer);
        return () =>
            document.removeEventListener('mousedown', handleClickOutsideDrawer);
    }, [drawerOpen, selectedRow]);

    useEffect(() => {
        function handleClickOutside(event) {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target) &&
                !buttonRefs.current[dropdownOpenId]?.contains(event.target)
            ) {
                setDropdownOpenId(null);
            }
        }

        if (dropdownOpenId !== null) {
            document.addEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [dropdownOpenId]);

    // Dropdown positioning state
    const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });

    // Toggle the per‐row dropdown menu
    const toggleDropdown = (id) => {
        if (dropdownOpenId === id) {
            setDropdownOpenId(null);
        } else {
            const btn = buttonRefs.current[id];
            if (btn) {
                const rect = btn.getBoundingClientRect();
                const dropdownWidth = 150;
                const dropdownHeight = 100;

                let left = rect.left + window.scrollX - dropdownWidth + rect.width;
                if (left < window.scrollX + 8) {
                    left = window.scrollX + 8;
                }

                let top = rect.bottom + window.scrollY;
                if (top + dropdownHeight > window.scrollY + window.innerHeight) {
                    top = rect.top + window.scrollY - dropdownHeight;
                }

                setDropdownPosition({ top, left });
            }
            setDropdownOpenId(id);
        }
    };

    // Commented out archived modal related code
    /*
    const archivedRef = useRef(null);
    const [archivedModalOpen, setArchivedModalOpen] = useState(false);

    const openArchiveModal = (branchId) => {
        setSelectedBranchId(branchId);
        handleArchivedConfirm(branchId);
    };

    const handleArchivedConfirm = async (branchId) => {
        if (!branchId) {
            console.error("No branch ID selected");
            return;
        }

        try {
            const response = await apiHelper(`entities/branches/${branchId}/archive/`, 'POST');

            if (response.ok) {
                const data = await response.json();
                console.log("Branch archived successfully:", data);
                setSuccessMessage(data.detail || 'Branch archived successfully.');
                setBranchData(prev => prev.filter(branch => branch.branch_id !== branchId));
                fetchArchivedBranches();
            } else {
                const error = await response.json();
                console.error("Error archiving branch:", error);
            }
        } catch (err) {
            console.error("Network/server error:", err);
        }

        setArchivedModalOpen(false);
    };

    const fetchArchivedBranches = async () => {
        try {
            const response = await apiHelper('entities/branches/?archived=true', 'GET');
            if (response.ok) {
                const data = await response.json();
                console.log("Archived branches fetched:", data);
                setArchivedData(data);
            } else {
                const error = await response.json();
                console.error("Failed to fetch archived branches:", error);
            }
        } catch (error) {
            console.error("Error fetching archived branches:", error);
        }
    };

    useEffect(() => {
        if (activeTab === 'archived-branch') {
            fetchArchivedBranches();
        }
    }, [activeTab]);
    */

    const editBranch = () => {
        if (selectedIds.length === 1) {
            history.push(`/branches/edit-branch/${selectedIds[0]}`);
        } else if (dropdownOpenId) {
            history.push(`/branches/edit-branch/${dropdownOpenId}`);
        } else if (selectedRow?.branch_id) {
            history.push(`/branches/edit-branch/${selectedRow.branch_id}`);
        } else {
            alert("Please select a branch to edit.");
        }
    };

    // Updated handleDeactivateBranch function with confirmation modal
    const handleDeactivateBranch = async (branchId) => {
        if (!branchId) {
            console.error("No branch ID found");
            return;
        }

        const row = pageData.find(d => String(d.branch_id) === String(branchId));
        if (!row) return;

        // If activating, show toast directly
        if (row.status === false || row.status === 'Inactive') {
            await activateBranch(branchId);
            return;
        }

        // If deactivating, show confirmation modal
        setBranchToDeactivate(branchId);
        setShowConfirmModal(true);
    };

    // New function to handle activation
    const activateBranch = async (branchId) => {
        try {
            showLoader();
            const response = await apiHelper(`entities/branches-update-status/${branchId}/`, 'POST');

            if (response.ok) {
                const data = await response.json();
                console.log("Branch activated successfully:", data);

                setBranchData(prev =>
                    prev.map(branch =>
                        branch.branch_id === branchId
                            ? { ...branch, status: true, status_text: 'Active' }
                            : branch
                    )
                );

                // Update selectedRow if it's the same branch
                setSelectedRow(prev =>
                    prev && prev.branch_id === branchId
                        ? { ...prev, status: true, status_text: 'Active' }
                        : prev
                );

                // Show activation toast
                setToastAction('activate');
                setShowToast(true);

                setDropdownOpenId(null);
            } else {
                const error = await response.json();
                console.error("Error activating branch:", error);
            }
        } catch (err) {
            console.error("Network/server error while activating branch:", err);
        } finally {
            hideLoader();
        }
    };

    // New function to handle confirmed deactivation
    const confirmDeactivate = async () => {
        if (!branchToDeactivate) return;

        try {
            showLoader();
            const response = await apiHelper(`entities/branches-update-status/${branchToDeactivate}/`, 'POST');

            if (response.ok) {
                const data = await response.json();
                console.log("Branch deactivated successfully:", data);

                setBranchData(prev =>
                    prev.map(branch =>
                        branch.branch_id === branchToDeactivate
                            ? { ...branch, status: false, status_text: 'Inactive' }
                            : branch
                    )
                );

                // Update selectedRow if it's the same branch
                setSelectedRow(prev =>
                    prev && prev.branch_id === branchToDeactivate
                        ? { ...prev, status: false, status_text: 'Inactive' }
                        : prev
                );

                // Show deactivation toast
                setToastAction('deactivate');
                setShowToast(true);

                setDropdownOpenId(null);
            } else {
                const error = await response.json();
                console.error("Error deactivating branch:", error);
            }
        } catch (err) {
            console.error("Network/server error while deactivating branch:", err);
        } finally {
            hideLoader();
            setShowConfirmModal(false);
            setBranchToDeactivate(null);
        }
    };

    // Handle toast close
    const handleToastClose = () => {
        setShowToast(false);
        setToastAction('');
    };

    const [toolbarDropdownOpen, setToolbarDropdownOpen] = useState(false);
    const toolbarRef = useRef(null);
    const toggleToolbarDropdown = () => {
        setToolbarDropdownOpen(prev => !prev);
    };

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (toolbarRef.current && !toolbarRef.current.contains(e.target)) {
                setToolbarDropdownOpen(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    const saveRef = useRef(null);
    const [saveModalOpen, setSaveModalOpen] = useState(false);
    const handleSaveConfirm = () => {
        setSaveModalOpen(false);
    };

    useEffect(() => {
        function handleClickOutside(event) {
            if (saveRef.current && !saveRef.current.contains(event.target)) {
                setSaveModalOpen(false);
            }
        }

        if (saveModalOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [saveModalOpen]);

    const handleSort = (field) => {
        if (sortField === field) {
            // toggle order
            setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
            console.log('this clicked');
        } else {
            setSortField(field);
            setSortOrder('asc');
        }
    };

    // Commented out archived checkbox handler
    /*
    const handleArchivedCheckboxChange = (setter, value) => (e) => {
        const isChecked = e.target.checked;
        setter(prev =>
            isChecked
                ? [...prev, value]
                : prev.filter(v => v !== value)
        );
    };
    */

    // Show Branch Not Available if no branches are available
    const showNoBranchesMessage = () => {
        if (loading) {
            return null; // Don't show message while loading
        }

        if (branchData.length === 0) {
            return (
                <div className="empty-state">
                    <div className="empty-state-content">
                        <div class="icon"><img
                            src={noBrandImage}
                            alt="avatar"
                            className="avatar"
                            style={{
                                width: 120,
                                height: 120,
                                verticalAlign: 'middle'
                            }}
                        /></div>
                        <h1 class="no-item-title mt-2">
                            No Branch Exist
                        </h1>
                        <p class="no-item-text">
                            Nothing here yet. Add your first item to get started!
                        </p>
                    </div>
                </div>
            );
        }

        if (activeSearchTerm && filtered.length === 0) {
            return (
                <div className="empty-state">
                    <div className="empty-state-content">
                        <div class="icon"><img
                            src={emptySearchImg}
                            alt="avatar"
                            className="avatar"
                            style={{
                                width: 120,
                                height: 120,
                                verticalAlign: 'middle'
                            }}
                        /></div>
                        <h1 class="no-item-title mt-2">
                            No Branch Found
                        </h1>
                        <p class="no-item-text">
                            No results match what you're looking for
                        </p>
                    </div>
                </div>
            );
        }

        return null;
    };

    // Helper function to check if field value is empty
    const isEmptyValue = (value) => {
        return value === null || value === undefined || value === '' || value === 'No Description';
    };

    // Helper function to render field value with NA fallback
    const renderFieldValue = (value) => {
        if (isEmptyValue(value)) {
            return <span style={{ color: '#ADB5BD' }}>NA</span>;
        }
        return value;
    };

    // Helper function to check if field content is overflowing
    const isFieldOverflowing = (value, threshold = 25) => {
        if (!value || isEmptyValue(value)) return false;
        return value.length > threshold;
    };

    // NEW: Improved grid row calculation for Basic Info section
    const getBasicInfoRow = (field, branchName, id, workplaceName) => {
        let baseRow = 1;

        // Calculate dynamic rows based on content length
        if (field === 'branchName') return baseRow;

        if (field === 'status') {
            // Status moves to next row if branch name is long
            return isFieldOverflowing(branchName) ? baseRow + 2 : baseRow;
        }

        if (field === 'id') {
            // ID starts after status row
            let row = isFieldOverflowing(branchName) ? baseRow + 3 : baseRow + 1;
            return row;
        }

        if (field === 'workplace') {
            // Workplace moves to next row if ID is long
            let row = isFieldOverflowing(branchName) ? baseRow + 3 : baseRow + 1;
            return isFieldOverflowing(id) ? row + 2 : row;
        }

        if (field === 'openid') {
            // Open ID always comes after workplace
            let row = isFieldOverflowing(branchName) ? baseRow + 3 : baseRow + 1;
            row = isFieldOverflowing(id) ? row + 2 : row;
            row = isFieldOverflowing(workplaceName) ? row + 2 : row;
            return row + 1; // Always one row after the last field
        }

        return baseRow;
    };

    // NEW: Improved grid row calculation for Location Info section
    const getLocationRow = (field, address1, address2, city, state, postalCode, country) => {
        let baseRow = 2; // Start from row 2 (row 1 is for Location Type)

        if (field === 'locationType') return 1;

        if (field === 'address1') return baseRow;

        if (field === 'address2') return baseRow + 1;

        if (field === 'city') {
            // City starts after address fields
            return baseRow + 2;
        }

        if (field === 'state') {
            // State on same row as city
            return baseRow + 2;
        }

        if (field === 'postcode') {
            // Post code after city/state row
            return baseRow + 3;
        }

        if (field === 'country') {
            // Country on same row as post code
            return baseRow + 3;
        }

        if (field === 'google') {
            // Google link after postcode/country row
            return baseRow + 4;
        }

        return baseRow;
    };

    // Custom sorting icon component
    const SortIcon = ({ field }) => {
        const isActive = sortField === field;
        const isAsc = sortOrder === 'asc';

        return (
            <svg
                xmlns="http://www.w3.org/2000/svg"
                width="12"
                height="12"
                viewBox="0 0 12 12"
                fill="none"
                style={{
                    marginLeft: '4px',
                    // opacity: isActive ? 1 : 0.3
                }}
            >
                <path
                    d="M8.59334 5.11111C8.9566 5.11111 9.13824 4.72222 8.87588 4.48148L6.29263 2.11111C6.13118 1.96296 5.86882 1.96296 5.70736 2.11111L3.12412 4.48148C2.86176 4.72222 3.0434 5.11111 3.40666 5.11111H8.59334Z"
                    fill={isActive && isAsc ? "#212529" : "#6C757D"}
                />
                <path
                    d="M3.40666 6.88867C3.0434 6.88867 2.86176 7.27756 3.12412 7.5183L5.70736 9.88867C5.86882 10.0368 6.13118 10.0368 6.29264 9.88867L8.87588 7.5183C9.13824 7.27756 8.9566 6.88867 8.59334 6.88867H3.40666Z"
                    fill={isActive && !isAsc ? "#212529" : "#6C757D"}
                />
            </svg>
        );
    };

    // Helper function to truncate text with ellipsis
    const truncateText = (text, maxLength = 25) => {
        if (!text || isEmptyValue(text)) return text;
        if (text.length <= maxLength) return text;
        return text.substring(0, maxLength) + '...';
    };


    const columns = [
        {
            label: "Branch Name",
            field: "branch_name",
            sortable: true,
            width: "20%",
        },
        {
            label: "Status",
            field: "status_text",
            sortable: true,
            width: "20%",
            render: (row) => (
                <span className={`status ${row.status_text?.toLowerCase()}`}>
                    {row.status_text || <span style={{ color: "#ADB5BD" }}>NA</span>}
                </span>
            )
        },
        {
            label: "ID",
            field: "id",
            sortable: true,
            width: "25%",
            render: (row) => row.id ? String(row.id) : <span style={{ color: "#ADB5BD" }}>NA</span>
        },
        {
            label: "Workplace",
            field: "workplace_name",
            sortable: true,
            width: "25%",
        },
    ];


    return (
        <div className="company-body new-look">
            {/* Alert Notification for activation/deactivation */}
            {showToast && (
                <AlertNotification
                    type="success"
                    recordName="Branch"
                    action={toastAction}
                    onClose={handleToastClose}
                />
            )}

            {/* Confirm Deactivate Modal */}
            <ConfirmDeactivateModal
                isOpen={showConfirmModal}
                onCancel={() => {
                    setShowConfirmModal(false);
                    setBranchToDeactivate(null);
                }}
                onConfirm={confirmDeactivate}
                entityName="Branch"
                title="Confirm to deactivate this Branch?"
                paragraph="After deactivating, you can't associate this branch with any StockInstance."
            />

            <div className="breadcrumb">
                {breadcrumbs.map((crumb, index) => (
                    <span key={index} className="breadcrumb-item">
                        {crumb}
                        {index !== breadcrumbs.length - 1}
                    </span>
                ))}
            </div>
           {successMessage && (
                           <AlertNotification
                               type="success"
                               recordName="Branch"
                               action={
                                   successMessage.includes("Archived")
                                       ? "archive"
                                       : successMessage.includes("Discarded")
                                           ? "discard"
                                           : successMessage.includes("Activated")
                                               ? "activate"
                                               : successMessage.includes("Deactivated")
                                                   ? "deactivate"
                                                   : "create"
                               }
                               onClose={() => setSuccessMessage("")}
                           />
                       )}
            <div className="employee-tabs-container">
                <div className="tabs">
                    <button
                        className={`tab ${activeTab === 'branches' ? 'active' : ''}`}
                        onClick={() => setActiveTab('branches')}
                    >
                        Branches
                    </button>
                    {/* Commented out archived branch tab
                    <button
                        className={`tab ${activeTab === 'archived-branch' ? 'active' : ''}`}
                        onClick={() => setActiveTab('archived-branch')}
                    >
                        Archived Branch
                    </button>
                    */}
                </div>

                {activeTab === 'branches' && (
                    <div className="tab-content" id="branches-content">
                        <div className="info-section">
                            <div className="info-card">
                                <div className="entities-tab">
                                    <div className="toolbar">
                                        <div className="toolbar-left">
                                            <SearchBox
                                                searchInput={searchTerm}
                                                setSearchInput={setSearchTerm}
                                                setSearchTerm={setActiveSearchTerm}
                                                setCurrentPage={setCurrentPage}
                                                disabled={branchData.length === 0}
                                            />



                                        </div>
                                        <div className="toolbar-right">
                                            {/* Commented out archive button
                                            <button
                                                className={`icon-btn dark-button ${selectedIds.length === 0 ? 'disabled' : ''}`}
                                                disabled={selectedIds.length === 0}
                                                onClick={async () => {
                                                    try {
                                                        const response = await apiHelper('entities/branches/archive/', 'POST', { branch_ids: selectedIds });
                                                        if (response.ok) {
                                                            const data = await response.json();
                                                            setSuccessMessage(data.message || 'Branches archived successfully.');
                                                            setBranchData(prev => prev.filter(branch => !selectedIds.includes(branch.branch_id)));
                                                            setSelectedIds([]);
                                                            fetchArchivedBranches();
                                                        } else {
                                                            const error = await response.json();
                                                            setValidationError([error.error || 'Failed to archive branches']);
                                                            setSuccessMessage('');
                                                        }
                                                    } catch (error) {
                                                        console.error('Error archiving branches:', error);
                                                        setValidationError(['An error occurred while archiving branches']);
                                                        setSuccessMessage('');
                                                    }
                                                }}
                                            >
                                                Archive
                                            </button>
                                            */}
                                            <button
                                                className="icon-btn dark-border"
                                                onClick={() => history.push("/branches/add-branch")}
                                            >
                                                <Plus size={18} /> Add Branch
                                            </button>
                                        </div>
                                    </div>

                                    {/* Commented out filter popup
                                    {filterPopupOpen && (
                                        <div
                                            ref={filterPopupRef}
                                            className="filter-popup"
                                            style={{
                                                position: 'absolute',
                                                top: filterPosition.top,
                                                left: filterPosition.left,
                                                background: '#fff',
                                                padding: '16px',
                                                borderRadius: '8px',
                                                width: '300px',
                                                boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
                                                zIndex: '1002',
                                                maxHeight: '350px',
                                                overflowY: 'auto',
                                                scrollbarWidth: 'none',
                                                msOverflowStyle: 'none'
                                            }}
                                        >
                                            <div className="filter-header" style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '16px' }}>
                                                <button className="close-btn" onClick={() => setFilterPopupOpen(false)}>
                                                    <X size={20} />
                                                </button>
                                            </div>
                                            <div className="filter-content">
                                                <div className="filter-section" style={{ marginBottom: '16px' }}>
                                                    <label style={{ display: 'block', marginBottom: '4px' }}>Status</label>
                                                    <div className="filter-options" style={{ maxHeight: '90px', overflowY: 'auto' }}>
                                                        {['Active', 'Inactive'].map(status => (
                                                            <label key={status} className="form-check" style={{ cursor: 'pointer', fontWeight: '400' }}>
                                                                <input
                                                                    type="checkbox"
                                                                    className="form-check-input"
                                                                    checked={selectedStatuses.includes(status)}
                                                                    onChange={handleCheckboxChange(setSelectedStatuses, status)}
                                                                />
                                                                <span style={{ marginLeft: '8px' }}>{status}</span>
                                                            </label>
                                                        ))}
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="filter-footer" style={{ textAlign: 'center', marginTop: '10px' }}>
                                                <button
                                                    className="filter-btn"
                                                    onClick={() => setFilterPopupOpen(false)}
                                                    style={{
                                                        background: 'black',
                                                        color: 'white',
                                                        padding: '8px 16px',
                                                        border: 'none',
                                                        borderRadius: '4px',
                                                        cursor: 'pointer'
                                                    }}
                                                >
                                                    Apply Filter
                                                </button>
                                            </div>
                                        </div>
                                    )} */}

                                    <div className="table-wrapper" style={{
                                        borderBottom:
                                            (activeSearchTerm && filtered.length === 0) ||
                                                branchData.length === 0
                                                ? "none"
                                                : "1px solid #dee2e6",
                                    }}>
                                        <DynamicTable
                                            columns={columns}
                                            data={pageData.filter(row => !row.is_archived)}
                                            sortField={sortField}
                                            sortOrder={sortOrder}
                                            handleSort={handleSort}
                                            onRowClick={(row) => {
                                                console.log("Row clicked", row);
                                                setSelectedRow(row);
                                                onRowClick(row);
                                            }}
                                            onActionClick={(id) => {
                                                console.log("Action clicked for ID:", id);
                                                toggleDropdown(id);
                                            }}
                                            showActions={true}
                                            showCheckbox={false}
                                            selectedIds={selectedIds}
                                            dropdownOpenId={dropdownOpenId}
                                            suppressHighlight={suppressHighlight}
                                            buttonRefs={buttonRefs}
                                            rowKey="branch_id"
                                            searchTerm={activeSearchTerm}

                                            emptyImage={
                                                activeSearchTerm && filtered.length === 0
                                                    ? emptySearchImg
                                                    : noBrandImage
                                            }
                                            emptyTitle={
                                                activeSearchTerm && filtered.length === 0
                                                    ? "No Branch Found"
                                                    : "No Branch Exist"
                                            }
                                            emptyMessage={
                                                activeSearchTerm && filtered.length === 0
                                                    ? "No results match what you're looking for"
                                                    : "Nothing here yet. Add your first item to get started!"
                                            }
                                        />
                                        {dropdownOpenId !== null && (
                                            <div
                                                ref={dropdownRef}
                                                className="backdrop-cls"
                                                style={{
                                                    position: "absolute",
                                                    top: dropdownPosition.top,
                                                    left: dropdownPosition.left,
                                                    background: "white",
                                                    boxShadow: "0px 1px 4px 0px #0000001A",
                                                    zIndex: 1000,
                                                    minWidth: 152,
                                                    borderRadius: 8,
                                                }}
                                                onClick={e => e.stopPropagation()}
                                            >
                                                <div className="btn-align-forall">
                                                    <div className="common-for-all">
                                                        <button
                                                            onClick={() => {
                                                                const row = pageData.find(d => d.branch_id === dropdownOpenId);
                                                                onRowClick(row);
                                                                setDropdownOpenId(null);
                                                            }}>
                                                            View Details
                                                        </button>
                                                    </div>
                                                    <div className="common-for-all">
                                                        <button
                                                            onClick={() => {
                                                                editBranch(dropdownOpenId);
                                                            }}
                                                        >
                                                            Edit
                                                        </button>
                                                    </div>
                                                    <div className="common-for-all">
                                                        {dropdownOpenId !== null && (() => {
                                                            const row = pageData.find(d => String(d.branch_id) === String(dropdownOpenId));
                                                            if (!row) return null;
                                                            return (
                                                                <button
                                                                    onClick={() => {
                                                                        handleDeactivateBranch(row.branch_id);
                                                                        setDropdownOpenId(null);
                                                                    }}
                                                                >
                                                                    {row.status === true || row.status === 'Active' ? 'Deactivate' : 'Activate'}
                                                                </button>
                                                            );
                                                        })()}
                                                    </div>
                                                    {/* Commented out archive option
                                                    <div className="common-for-all">
                                                        <button
                                                            onClick={() => {
                                                                const row = pageData.find(d => d.branch_id === dropdownOpenId);
                                                                if (row) {
                                                                    openArchiveModal(row.branch_id);
                                                                } else {
                                                                    console.warn("No matching row found for dropdownOpenId");
                                                                }
                                                                setDropdownOpenId(null);
                                                            }}
                                                            className="deactivate-cls"
                                                        >
                                                            Archive
                                                        </button>
                                                    </div>
                                                    */}
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    {filtered.length > 0 && (
                                        <div className="pagination">
                                            <div className="pagination-info">
                                                {`${(currentPage - 1) * PAGE_SIZE + 1} - ${Math.min(currentPage * PAGE_SIZE, filtered.length)} of ${filtered.length}`}
                                            </div>
                                           <Pagination
                                                currentPage={currentPage}
                                                totalPages={totalPages}
                                                onPageChange={setCurrentPage}
                                            />
                                        </div>
                                    )}

                                    {saveModalOpen && (
                                        <>
                                            <div className="drawer-overlay" />
                                            <div
                                                className="entity-drawer modal-popup-cls"
                                                ref={saveRef}
                                                style={{
                                                    maxWidth: 650,
                                                    margin: "0 auto",
                                                    background: "white",
                                                    borderRadius: 12,
                                                    zIndex: 1050,
                                                    padding: 24,
                                                    position: "fixed",
                                                    top: "50%",
                                                    left: "50%",
                                                    transform: "translate(-50%, -50%)",
                                                    boxShadow: "0px 2px 10px rgba(0, 0, 0, 0.2)",
                                                    height: "auto",
                                                    right: "auto",
                                                    overflow: "inherit",
                                                }}
                                            >
                                                <div className="drawer-header d-flex justify-content-end align-items-center pt-0 pe-0">
                                                    <button className="close-btn" onClick={() => setSaveModalOpen(false)}>
                                                        <X size={20} />
                                                    </button>
                                                </div>
                                                <h3 style={{ textAlign: "left", color: "#212529", fontSize: "20px", fontWeight: "600", lineHeight: "24px", fontFamily: "Montserrat", marginBottom: "5px" }}>New feature in Progress</h3>
                                                <p style={{ marginBottom: "0", textAlign: "left", color: "#6C757D", fontSize: "14px", fontWeight: "400", lineHeight: "20px", fontFamily: "Montserrat" }}>Work in Progress</p>
                                                <div className="drawer-footer" style={{ paddingTop: "10px" }}>
                                                    <button className="btn-save" onClick={handleSaveConfirm}>
                                                        OK
                                                    </button>
                                                </div>
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>

                        {drawerOpen && selectedRow && (
                            <>
                                <div className="drawer-overlay" />
                                <div className="entity-drawer" ref={drawerRef}>
                                    <div className="drawer-header" style={{ backgroundColor: "var(--Gray-100, #F8F9FA)" }}>
                                        <div className='d-flex align-items-center gap-1'>
                                            <h3>Branch Info</h3>
                                        </div>
                                        <button
                                            className="close-btn"
                                            onClick={() => {
                                                setDrawerOpen(false);
                                                setSelectedIds(ids => ids.filter(id => id !== selectedRow?.branch_id));
                                                setSelectedRow(null);
                                            }}
                                        >
                                            <X size={18} />
                                        </button>
                                    </div>
                                    <div className="main-drawer-inner">
                                        <div className="drawer-title d-flex align-items-center justify-content-between" style={{ borderBottom: 'none' }}>
                                            <div className='d-flex align-items-center' style={{ maxWidth: '320px', overflowWrap: 'anywhere' }}>
                                                <h2 className='pb-0' style={{ textTransform: 'none' }}>{selectedRow?.branch_name}</h2>
                                            </div>
                                            <div className='entities-tab'>
                                                <div className='toolbar'>
                                                    <div className='toolbar-right' style={{ position: "relative" }} ref={toolbarRef}>
                                                        <button
                                                            className="dark-border gray-btn mx-2"
                                                            type='button'
                                                            onClick={editBranch}
                                                            style={{ width: "100px" }}
                                                        >
                                                            Edit
                                                        </button>
                                                        <button
                                                            className="drawer-dots"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                toggleToolbarDropdown();
                                                            }}
                                                        >
                                                            <MoreHorizontal size={18} />
                                                        </button>

                                                        {toolbarDropdownOpen && (
                                                            <div
                                                                className="backdrop-cls"
                                                                style={{
                                                                    position: "absolute",
                                                                    top: 40,
                                                                    right: 0,
                                                                    background: "white",
                                                                    boxShadow: "0px 1px 4px 0px #0000001A",
                                                                    zIndex: 1000,
                                                                    minWidth: 152,
                                                                    borderRadius: 8,
                                                                }}
                                                                onClick={(e) => e.stopPropagation()}
                                                            >
                                                                <div className="btn-align-forall">
                                                                    <div className="common-for-all">
                                                                        <button
                                                                            onClick={() => {
                                                                                handleDeactivateBranch(selectedRow.branch_id);
                                                                                setToolbarDropdownOpen(false);
                                                                            }}
                                                                        >
                                                                            {selectedRow.status_text === 'Active' ? 'Deactivate' : 'Activate'}
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="drawer-tabs" style={{ marginBottom: '20px' }}>
                                            <button
                                                className={drawerTab === 'general' ? 'active' : ''}
                                                onClick={() => setDrawerTab('general')}
                                            >
                                                General
                                            </button>
                                            <button
                                                className={drawerTab === 'additional' ? 'active' : ''}
                                                onClick={() => setDrawerTab('additional')}
                                            >
                                                Additional
                                            </button>
                                        </div>
                                        <div className="drawer-content">
                                            {drawerTab === 'general' && (
                                                <div className="tab-panel general-info-panel">
                                                    {/* Basic Info Section */}
                                                    <div style={{
                                                        background: 'var(--Background-White, #FFF)',
                                                        marginBottom: '10px',
                                                        boxShadow: 'rgba(0, 0, 0, 0.08) 0px 4px 12px',
                                                        borderRadius: '12px'
                                                    }}>
                                                        <div style={{
                                                            borderBottom: '.0625rem solid rgba(46, 54, 80, .125)',
                                                            color: 'var(--Gray-900, #212529)',
                                                            padding: '20px 15px',
                                                            fontFamily: 'Montserrat',
                                                            fontSize: '14px',
                                                            fontWeight: 600,
                                                            lineHeight: 'normal',
                                                            background: '#F8F9FA',
                                                            borderRadius: '12px 12px 0 0'
                                                        }}>
                                                            Basic Info
                                                        </div>
                                                        <div style={{
                                                            display: 'grid',
                                                            gridTemplateColumns: 'repeat(2, 1fr)',
                                                            gap: '20px',
                                                            padding: '20px',
                                                            borderRadius: '0 0 12px 12px',
                                                            gridAutoRows: 'min-content',
                                                            alignItems: 'start'
                                                        }}>
                                                            {/* Branch Name - Always full width when lengthy */}
                                                            <div className="field-block" style={{
                                                                borderBottom: 'none',
                                                                display: 'flex',
                                                                flexDirection: 'column',
                                                                gridColumn: isFieldOverflowing(selectedRow.branch_name) ? '1 / -1' : '1',
                                                                gridRow: 1,
                                                                padding: '0px'
                                                            }}>
                                                                <div className="field-label">Branch Name</div>
                                                                <div className="field-value" style={{
                                                                    wordWrap: 'break-word',
                                                                    overflowWrap: 'anywhere',
                                                                    whiteSpace: 'normal'
                                                                }}>
                                                                    {renderFieldValue(selectedRow.branch_name)}
                                                                </div>
                                                            </div>

                                                            {/* Status - Moves to row 2 when branch name is lengthy */}
                                                            <div className="field-block" style={{
                                                                borderBottom: 'none',
                                                                display: 'flex',
                                                                flexDirection: 'column',
                                                                gridColumn: isFieldOverflowing(selectedRow.branch_name) ? '1' : '2',
                                                                gridRow: isFieldOverflowing(selectedRow.branch_name) ? 2 : 1,
                                                                padding: '0px'
                                                            }}>
                                                                <div className="field-label">Status</div>
                                                                <div className="field-value">
                                                                    <span className={`status ${typeof selectedRow?.status_text === 'string' ? selectedRow.status_text?.toLowerCase() : ''}`}>
                                                                        {renderFieldValue(selectedRow.status_text)}
                                                                    </span>
                                                                </div>
                                                            </div>

                                                            {/* ID - Always full width when lengthy */}
                                                            <div className="field-block" style={{
                                                                borderBottom: 'none',
                                                                display: 'flex',
                                                                flexDirection: 'column',
                                                                gridColumn: isFieldOverflowing(selectedRow.id?.toString()) ? '1 / -1' : '1',
                                                                gridRow: isFieldOverflowing(selectedRow.branch_name) ? 3 : 2,
                                                                padding: '0px'
                                                            }}>
                                                                <div className="field-label">ID</div>
                                                                <div className="field-value" style={{
                                                                    wordWrap: 'break-word',
                                                                    overflowWrap: 'anywhere',
                                                                    whiteSpace: 'normal'
                                                                }}>
                                                                    {renderFieldValue(selectedRow.id)}
                                                                </div>
                                                            </div>

                                                            {/* Workplace - Moves to row 4 when ID is lengthy */}
                                                            <div className="field-block" style={{
                                                                borderBottom: 'none',
                                                                display: 'flex',
                                                                flexDirection: 'column',
                                                                gridColumn: isFieldOverflowing(selectedRow.id?.toString()) ? '1' : '2',
                                                                gridRow: isFieldOverflowing(selectedRow.id?.toString()) ?
                                                                    (isFieldOverflowing(selectedRow.branch_name) ? 4 : 3) :
                                                                    (isFieldOverflowing(selectedRow.branch_name) ? 3 : 2),
                                                                padding: '0px'
                                                            }}>
                                                                <div className="field-label">Workplace</div>
                                                                <div className="field-value">
                                                                    {selectedRow.workplace_name ? (
                                                                        <div
                                                                            onClick={() =>
                                                                                history.push(
                                                                                    `/company?tab=workplaces&id=${selectedRow.workplace}&autoOpen=true&from=branch&branchId=${selectedRow.branch_id}`
                                                                                )
                                                                            }
                                                                            className="d-flex align-items-center gap-1 me-3"
                                                                            style={{
                                                                                color: "#2C7BE5",
                                                                                textDecoration: "underline",
                                                                                fontWeight: "600",
                                                                                fontSize: "12px",
                                                                                cursor: "pointer",
                                                                            }}
                                                                        >
                                                                            {selectedRow.workplace_name}
                                                                            <SquareArrowOutUpRight size={14} strokeWidth={3} />
                                                                        </div>
                                                                    ) : (
                                                                        <span style={{ color: '#ADB5BD' }}>NA</span>
                                                                    )}
                                                                </div>
                                                            </div>

                                                            {/* Open ID - Always full width */}
                                                            <div className="field-block" style={{
                                                                borderBottom: 'none',
                                                                padding: '0px',
                                                                gridColumn: '1 / -1',
                                                                display: 'flex',
                                                                flexDirection: 'column',
                                                                alignItems: 'flex-start',
                                                                width: '100%',
                                                                gridRow: isFieldOverflowing(selectedRow.id?.toString()) ?
                                                                    (isFieldOverflowing(selectedRow.branch_name) ? 5 : 4) :
                                                                    (isFieldOverflowing(selectedRow.branch_name) ? 4 : 3)
                                                            }}>
                                                                <div className="field-label">Open ID</div>
                                                                {selectedRow.uuid ? (
                                                                    <div className="field-value d-flex align-items-center gap-2" style={{
                                                                        wordWrap: 'break-word',
                                                                        overflowWrap: 'break-word',
                                                                        whiteSpace: 'normal',
                                                                        flexWrap: 'wrap',
                                                                        lineHeight: 'normal',
                                                                        fontWeight: 600,
                                                                        fontSize: '12px'
                                                                    }}>
                                                                        <div style={{ position: "relative", display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
                                                                            <span
                                                                                style={{
                                                                                    color: copied ? "#60aaffff" : "#2C7BE5",
                                                                                    cursor: "pointer",
                                                                                    textDecoration: "none",
                                                                                    transition: "color 0.3s",
                                                                                    marginRight: "4px",
                                                                                    wordBreak: 'break-all'
                                                                                }}
                                                                                onClick={() => handleCopy(selectedRow.uuid)}
                                                                            >
                                                                                {selectedRow.uuid}
                                                                            </span>
                                                                            <Copy
                                                                                size={13}
                                                                                onClick={() => handleCopy(selectedRow.uuid)}
                                                                                style={{ cursor: "pointer", color: copied ? "#60aaffff" : "#2C7BE5", flexShrink: 0 }}
                                                                            />
                                                                            {copied && <span style={{ fontSize: '12px', color: "#2C7BE5", flexShrink: 0 }}>Copied!</span>}
                                                                        </div>
                                                                    </div>
                                                                ) : <span style={{ color: '#ADB5BD' }}>NA</span>}
                                                            </div>
                                                        </div>
                                                    </div>

                                                    {/* Location Info Section */}
                                                    <div style={{
                                                        background: 'var(--Background-White, #FFF)',
                                                        border: '1px solid #ffffff',
                                                        borderRadius: '12px',
                                                        boxShadow: 'rgba(0, 0, 0, 0.08) 0px 4px 12px'
                                                    }}>
                                                        <div style={{
                                                            borderBottom: '.0625rem solid rgba(46, 54, 80, .125)',
                                                            color: 'var(--Gray-900, #212529)',
                                                            padding: '20px 15px',
                                                            fontFamily: 'Montserrat',
                                                            fontSize: '14px',
                                                            fontWeight: 600,
                                                            lineHeight: 'normal',
                                                            background: '#F8F9FA',
                                                            borderRadius: '12px 12px 0 0'
                                                        }}>
                                                            Location Info
                                                        </div>
                                                        <div style={{
                                                            display: 'flex',
                                                            flexDirection: 'column',
                                                            gap: '20px',
                                                            borderRadius: '0 0 12px 12px',
                                                            padding: '20px'
                                                        }}>
                                                            {/* Location Type */}
                                                            <div className="field-block" style={{
                                                                borderBottom: 'none',
                                                                padding: '0px',
                                                                display: 'flex',
                                                                flexDirection: 'column',
                                                                alignItems: 'flex-start',
                                                                width: '100%',

                                                            }}>
                                                                <div className="field-label">Location Type</div>
                                                                <div className="field-value">
                                                                    {selectedRow.location_type ? (
                                                                        <span className={`status`} style={{ background: '#E7E8E8', border: '1px solid #343A40' }}>
                                                                            {renderFieldValue(selectedRow.location_type)}
                                                                        </span>
                                                                    ) : (
                                                                        <span style={{ color: '#ADB5BD' }}>NA</span>
                                                                    )}
                                                                </div>
                                                            </div>

                                                            {/* Only show address fields if location type is not Virtual */}
                                                            {selectedRow.location_type !== 'Virtual' && (
                                                                <>
                                                                    {/* Address Line 1 */}
                                                                    <div className="field-block" style={{
                                                                        borderBottom: 'none',
                                                                        display: 'flex',
                                                                        flexDirection: 'column',
                                                                        padding: '0px'
                                                                    }}>
                                                                        <div className="field-label">Address Line 1</div>
                                                                        <div className="field-value" style={{
                                                                            wordWrap: 'break-word',
                                                                            overflowWrap: 'break-word',
                                                                            whiteSpace: 'normal',
                                                                            width: '100%'
                                                                        }}>
                                                                            {renderFieldValue(selectedRow.address?.street_address)}
                                                                        </div>
                                                                    </div>

                                                                    {/* Address Line 2 */}
                                                                    <div className="field-block" style={{
                                                                        borderBottom: 'none',
                                                                        display: 'flex',
                                                                        flexDirection: 'column',
                                                                        padding: '0px'
                                                                    }}>
                                                                        <div className="field-label">Address Line 2</div>
                                                                        <div className="field-value" style={{
                                                                            wordWrap: 'break-word',
                                                                            overflowWrap: 'break-word',
                                                                            whiteSpace: 'normal',
                                                                            width: '100%'
                                                                        }}>
                                                                            {renderFieldValue(selectedRow.address?.street_address_2)}
                                                                        </div>
                                                                    </div>

                                                                    {/* City, State, Post Code, Country in responsive grid */}
                                                                    <div style={{
                                                                        display: 'grid',
                                                                        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                                                                        gap: '20px'
                                                                    }}>
                                                                        {/* City */}
                                                                        <div className="field-block" style={{
                                                                            borderBottom: 'none',
                                                                            display: 'flex',
                                                                            flexDirection: 'column',
                                                                            padding: '0px'
                                                                        }}>
                                                                            <div className="field-label">City</div>
                                                                            <div className="field-value" style={{
                                                                                wordWrap: 'break-word',
                                                                                overflowWrap: 'break-word',
                                                                                whiteSpace: 'normal'
                                                                            }}>
                                                                                {renderFieldValue(selectedRow.address?.city)}
                                                                            </div>
                                                                        </div>

                                                                        {/* State */}
                                                                        <div className="field-block" style={{
                                                                            borderBottom: 'none',
                                                                            display: 'flex',
                                                                            flexDirection: 'column',
                                                                            padding: '0px'
                                                                        }}>
                                                                            <div className="field-label">State</div>
                                                                            <div className="field-value" style={{
                                                                                wordWrap: 'break-word',
                                                                                overflowWrap: 'break-word',
                                                                                whiteSpace: 'normal'
                                                                            }}>
                                                                                {renderFieldValue(selectedRow.address?.state)}
                                                                            </div>
                                                                        </div>

                                                                        {/* Post Code */}
                                                                        <div className="field-block" style={{
                                                                            borderBottom: 'none',
                                                                            display: 'flex',
                                                                            flexDirection: 'column',
                                                                            padding: '0px'
                                                                        }}>
                                                                            <div className="field-label">Post Code</div>
                                                                            <div className="field-value" style={{
                                                                                wordWrap: 'break-word',
                                                                                overflowWrap: 'break-word',
                                                                                whiteSpace: 'normal'
                                                                            }}>
                                                                                {renderFieldValue(selectedRow.address?.postal_code)}
                                                                            </div>
                                                                        </div>

                                                                        {/* Country */}
                                                                        <div className="field-block" style={{
                                                                            borderBottom: 'none',
                                                                            display: 'flex',
                                                                            flexDirection: 'column',
                                                                            padding: '0px'
                                                                        }}>
                                                                            <div className="field-label">Country</div>
                                                                            <div className="field-value" style={{
                                                                                wordWrap: 'break-word',
                                                                                overflowWrap: 'break-word',
                                                                                whiteSpace: 'normal'
                                                                            }}>
                                                                                {renderFieldValue(selectedRow.address?.country)}
                                                                            </div>
                                                                        </div>
                                                                    </div>

                                                                    {/* Google Link */}
                                                                    <div className="field-block" style={{
                                                                        borderBottom: 'none',
                                                                        padding: '0px',
                                                                        display: 'flex',
                                                                        flexDirection: 'column',
                                                                        alignItems: 'flex-start',
                                                                        width: '100%'
                                                                    }}>
                                                                        <div className="field-label">Google Link</div>
                                                                        <div className="field-value">
                                                                            {selectedRow?.address?.google_map_link ? (
                                                                                <a
                                                                                    href={selectedRow?.address?.google_map_link}
                                                                                    target="_blank"
                                                                                    rel="noopener noreferrer"
                                                                                    className="d-flex align-items-center gap-1 me-3"
                                                                                    style={{ color: '#2C7BE5', textDecoration: 'underline', fontWeight: '600', fontSize: '12px' }}
                                                                                >
                                                                                    {selectedRow?.address?.google_map_link}
                                                                                    <SquareArrowOutUpRight size={14} strokeWidth={3} />
                                                                                </a>
                                                                            ) : (
                                                                                <span style={{ color: '#ADB5BD' }}>NA</span>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                </>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                            {drawerTab === 'additional' && (
                                                <div className="tab-panel additional-info-panel" style={{
                                                    borderRadius: '12px',
                                                    marginTop: '20px',
                                                    boxShadow: 'rgba(0, 0, 0, 0.08) 0px 4px 12px'
                                                }}>
                                                    <p className="note-type-added" style={{
                                                        fontSize: '14px',
                                                        background: 'var(--Gray-100, #F8F9FA)',
                                                        borderRadius: '12px 12px 0 0',
                                                        borderBottom: '0.0625rem solid rgba(46, 54, 80, 0.125)',
                                                        padding: '20px 15px',
                                                        fontFamily: 'Montserrat',
                                                        fontWeight: 600,
                                                        lineHeight: 'normal',
                                                        margin: 0
                                                    }}>
                                                        Notes
                                                    </p>
                                                    <label className="no-description-cls" style={{
                                                        padding: "20px",
                                                        background: "var(--Background-White, #FFF)",
                                                        borderRadius: '0 0 12px 12px',
                                                        display: 'block',
                                                        minHeight: '60px'
                                                    }}>
                                                        {selectedRow?.notes ? selectedRow.notes : <span style={{ color: '#ADB5BD' }}>NA</span>}
                                                    </label>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}

                        {/* Commented out archived modal
                        {archivedModalOpen && (
                            <>
                                <div className="drawer-overlay" />
                                <div className="entity-drawer modal-popup-cls" ref={archivedRef}
                                    style={{
                                        maxWidth: 650,
                                        margin: "0 auto",
                                        background: "white",
                                        borderRadius: 12,
                                        zIndex: 1050,
                                        padding: 24,
                                        position: "fixed",
                                        top: "50%",
                                        left: "50%",
                                        transform: "translate(-50%, -50%)",
                                        boxShadow: "0px 2px 10px rgba(0, 0, 0, 0.2)",
                                        height: "auto",
                                        right: "auto",
                                        overflow: "inherit",
                                    }}>
                                    <div className="drawer-header d-flex justify-content-end align-items-center pt-0 pe-0">
                                        <button className="close-btn" onClick={() => setArchivedModalOpen(false)}>
                                            <X size={20} />
                                        </button>
                                    </div>
                                    <h3 style={{ textAlign: "left", color: "#212529", fontSize: "20px", fontWeight: "600", lineHeight: "24px", fontFamily: "Montserrat", marginBottom: "5px" }}>Branch can't be archived</h3>
                                    <p style={{ marginBottom: "0", textAlign: "left", color: "#6C757D", fontSize: "14px", fontWeight: "400", lineHeight: "20px", fontFamily: "Montserrat" }}>
                                        Branch stocked with items can't be archived, clear stock before archiving. Archive action is also subject to approval
                                    </p>
                                    <div className="drawer-footer" style={{ paddingTop: "10px" }}>
                                        <button className="btn-save" onClick={handleArchivedConfirm}>
                                            OK
                                        </button>
                                    </div>
                                </div>
                            </>
                        )}
                        */}
                    </div>
                )}

                {/* Commented out archived branch tab content
                {activeTab === 'archived-branch' && (
                    <div className="tab-content" id="archived-branch-content">
                        Archived branch content commented out...
                    </div>
                )}
                */}
            </div>
        </div>
    );
};